/**
 * The motto, as an object you can look at.
 *
 * "Every application, tailored. Every resume, honest." is two claims pulling
 * against each other — change the words, keep the facts — and that tension is
 * spatial: the same facts enter at the bottom, pass through a rewriting layer,
 * and leave at the top with nothing added and nothing lost.
 *
 * Labels sit FLAT, outside the 3D transform, connected to their plane by a
 * leader line. An earlier version set them on the planes themselves, where the
 * isometric foreshortening made them unreadable — which is the normal failure
 * of text on a rotated surface, and why technical drawings have always put
 * callouts beside the geometry rather than on it.
 *
 * Built in CSS 3D rather than as a rendered image: drawn from the product's own
 * vocabulary in its own colours, crisp at any size, weighs nothing, and cannot
 * go stale when the brand moves. Idle drift is motion-safe gated.
 *
 * ── Lighting ─────────────────────────────────────────────────────────────
 * The object used to be lit blue-white while the section behind it had gone
 * warm, which is what makes a graphic read as a sticker rather than something
 * standing in the scene. The fix is not to repaint the planes — their colours
 * carry meaning (green is verified, blue is the rewriting step) — it is to
 * relight them. Materials keep their colour; the LIGHT is warm, so every plane
 * catches dawn on its leading edge, the same edge everything else on this site
 * catches it on.
 *
 * `backdrop-blur` came off entirely. A translucent plane blurring the two
 * planes behind it smeared their edges into a white band across the middle of
 * the object, which read as a rendering fault rather than as glass. Glass that
 * you are meant to see THROUGH — the whole point here is that the facts are
 * visible at every layer — should not frost what is behind it.
 */
const FACTS = ["Acme", "2019–2024", "Kubernetes", "40%"];

const PLANES = [
  {
    key: "out",
    z: 150,
    label: "Tailored",
    note: "same facts, new words",
    accent: "text-[#5ee9b5]",
    dot: "bg-[#5ee9b5]",
    surface: "bg-verified/[0.13] border-verified/50",
    top: "6%",
  },
  {
    key: "layer",
    z: 75,
    label: "Rewriting",
    note: "the only thing that changes",
    accent: "text-[#a9c2ff]",
    dot: "bg-[#a9c2ff]",
    surface: "bg-[rgba(88,140,255,0.10)] border-[rgba(88,140,255,0.40)]",
    top: "40%",
  },
  {
    key: "paper",
    z: 0,
    label: "Your resume",
    note: "the facts you actually have",
    accent: "text-white/80",
    dot: "bg-white/60",
    surface: "bg-white/[0.06] border-white/25",
    top: "74%",
  },
];

export default function TruthStack() {
  return (
    <div className="relative w-full max-w-[620px]" data-testid="truth-stack">
      {/* The object. Purely geometry — no text lives inside the transform. */}
      <div className="relative aspect-[7/5]" aria-hidden="true">
        <div
          className="absolute inset-0 motion-safe:animate-[truthdrift_9s_ease-in-out_infinite]"
          style={{ perspective: "1200px", perspectiveOrigin: "50% 40%" }}
        >
          <div
            className="relative w-full h-full"
            style={{
              transformStyle: "preserve-3d",
              transform: "scale(0.64) rotateX(54deg) rotateZ(-38deg)",
            }}
          >
            {PLANES.map((p) => (
              <div
                key={p.key}
                className={`absolute inset-0 border rounded-sm shadow-[0_28px_64px_-30px_rgba(0,0,0,0.9)] ${p.surface}`}
                style={{
                  transform: `translateZ(${p.z}px)`,
                  borderLeftColor: "rgba(255,177,128,0.55)",
                  borderBottomColor: "rgba(255,177,128,0.3)",
                }}
              >
                {/* Abstracted text lines: the shape of a document, not
                    lettering that would be unreadable at this angle. */}
                <div className="p-6 space-y-2">
                  {[100, 82, 91, 64].map((w, i) => (
                    <div
                      key={i}
                      className={`h-1.5 rounded-full ${
                        p.key === "layer" ? "bg-[rgba(169,194,255,0.4)]" : "bg-white/20"
                      }`}
                      style={{ width: `${w}%` }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Callouts, flat and legible, with a leader line into the stack. */}
      <div className="absolute inset-0 pointer-events-none">
        {PLANES.map((p) => (
          <div
            key={p.key}
            className="absolute left-0 flex items-center gap-3"
            style={{ top: p.top }}
          >
            <div>
              <div className={`font-mono text-[11px] uppercase tracking-[0.16em] ${p.accent}`}>
                {p.label}
              </div>
              <div className="text-[11px] text-white/60 mt-0.5">{p.note}</div>
            </div>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${p.dot}`} />
            <span className="w-10 sm:w-16 h-px bg-gradient-to-r from-white/30 to-transparent" />
          </div>
        ))}
      </div>

      {/* The claim the object is making, stated once, in words. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[11px]">
        <span className="text-white/60">
          facts in <span className="text-white">{FACTS.length}</span>
        </span>
        <span className="text-white/60">
          facts out <span className="text-white">{FACTS.length}</span>
        </span>
        <span className="text-[#5ee9b5]">invented 0</span>
      </div>
    </div>
  );
}
